import { useState } from 'react';
import {
  ActionIcon,
  Box,
  ColorInput,
  Flex,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { IconBold, IconItalic } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type {
  ExportBrandingConfigData,
  FontConfig,
  HeadingStyleConfig,
  TypographyConfig,
} from '../types';
import FontSelect from './FontSelect';
import SectionHeader from './SectionHeader';
import StyleRow from './StyleRow';

type Props = {
  config: ExportBrandingConfigData;
  onChange: (config: ExportBrandingConfigData) => void;
};

type StyleDef =
  | { key: 'normal' | 'body_text'; label: string; type: 'body' }
  | {
      key: 'heading_1' | 'heading_2' | 'heading_3' | 'heading_4';
      label: string;
      type: 'heading';
    };

const STYLES: StyleDef[] = [
  { key: 'normal', label: 'Normal', type: 'body' },
  { key: 'body_text', label: 'Body text', type: 'body' },
  { key: 'heading_1', label: 'Heading 1', type: 'heading' },
  { key: 'heading_2', label: 'Heading 2', type: 'heading' },
  { key: 'heading_3', label: 'Heading 3', type: 'heading' },
  { key: 'heading_4', label: 'Heading 4', type: 'heading' },
];

function BodyControls({
  value,
  onChange,
}: {
  value: FontConfig;
  onChange: (v: FontConfig) => void;
}) {
  return (
    <Stack gap="sm">
      <Group grow>
        <FontSelect
          value={value.font_family}
          onChange={v => onChange({ ...value, font_family: v })}
        />
        <NumberInput
          label="Size (pt)"
          value={value.font_size_pt}
          onChange={v => onChange({ ...value, font_size_pt: Number(v) || 11 })}
          min={6}
          max={36}
          step={0.5}
        />
      </Group>
      <Flex align="center" gap="sm">
        <ColorInput
          label="Text colour"
          format="hex"
          value={`#${value.text_color}`}
          onChange={v => onChange({ ...value, text_color: v.replace('#', '') })}
          style={{ flex: 1 }}
        />
        <Flex align="center" gap={4} mt={24}>
          <ActionIcon
            variant={value.bold ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, bold: !value.bold })}
            size="lg"
          >
            <IconBold size={18} />
          </ActionIcon>
          <ActionIcon
            variant={value.italic ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, italic: !value.italic })}
            size="lg"
          >
            <IconItalic size={18} />
          </ActionIcon>
        </Flex>
      </Flex>
    </Stack>
  );
}

function HeadingControls({
  value,
  onChange,
}: {
  value: HeadingStyleConfig;
  onChange: (v: HeadingStyleConfig) => void;
}) {
  return (
    <Stack gap="sm">
      <Group grow>
        <FontSelect
          value={value.font_family}
          onChange={v => onChange({ ...value, font_family: v })}
        />
        <NumberInput
          label="Size (pt)"
          value={value.font_size_pt}
          onChange={v => onChange({ ...value, font_size_pt: Number(v) || 11 })}
          min={6}
          max={72}
          step={0.5}
        />
      </Group>
      <Flex align="center" gap="sm">
        <ColorInput
          label="Text colour"
          format="hex"
          value={`#${value.text_color}`}
          onChange={v => onChange({ ...value, text_color: v.replace('#', '') })}
          style={{ flex: 1 }}
        />
        <Flex align="center" gap={4} mt={24}>
          <ActionIcon
            variant={value.bold ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, bold: !value.bold })}
            size="lg"
          >
            <IconBold size={18} />
          </ActionIcon>
          <ActionIcon
            variant={value.italic ? 'filled' : 'default'}
            onClick={() => onChange({ ...value, italic: !value.italic })}
            size="lg"
          >
            <IconItalic size={18} />
          </ActionIcon>
        </Flex>
      </Flex>
      <Group>
        <Switch
          label="Bottom border"
          checked={value.bottom_border_enabled}
          onChange={e => onChange({ ...value, bottom_border_enabled: e.currentTarget.checked })}
        />
        {value.bottom_border_enabled && (
          <ColorInput
            label="Border colour"
            format="hex"
            value={`#${value.bottom_border_color}`}
            onChange={v => onChange({ ...value, bottom_border_color: v.replace('#', '') })}
            style={{ flex: 1 }}
          />
        )}
      </Group>
    </Stack>
  );
}

export default function TypographySection({ config, onChange }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = (key: string) => setExpanded(prev => (prev === key ? null : key));

  const handleTypographyChange = (updated: TypographyConfig) => {
    onChange({ ...config, typography: updated });
  };

  return (
    <div>
      <SectionHeader
        title="Typography"
        subtitle="Configure the brand fonts and per-style sizing used by every export."
      />
      <Stack gap="lg">
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} mb="xs" c="gray.7">
            Brand fonts
          </Text>
          <Group grow>
            <FontSelect
              label="Heading font"
              value={config.typography.font_head}
              onChange={v => handleTypographyChange({ ...config.typography, font_head: v })}
            />
            <FontSelect
              label="Body font"
              value={config.typography.font_body}
              onChange={v => handleTypographyChange({ ...config.typography, font_body: v })}
            />
          </Group>
        </Box>
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} mb="xs" c="gray.7">
            Per-style overrides
          </Text>
          <Stack gap="xxs">
            {STYLES.map(style => {
              const value = config[style.key];
              const traits = [
                `${value.font_family}`,
                `${value.font_size_pt}pt`,
                ...(value.bold ? ['Bold'] : []),
                ...(value.italic ? ['Italic'] : []),
              ];
              const summary = traits.join(', ');

              return (
                <StyleRow
                  key={style.key}
                  label={style.label}
                  summary={summary}
                  colorHex={value.text_color}
                  expanded={expanded === style.key}
                  onToggle={() => toggle(style.key)}
                >
                  {style.type === 'body' ? (
                    <BodyControls
                      value={value as FontConfig}
                      onChange={v => onChange({ ...config, [style.key]: v })}
                    />
                  ) : (
                    <HeadingControls
                      value={value as HeadingStyleConfig}
                      onChange={v => onChange({ ...config, [style.key]: v })}
                    />
                  )}
                </StyleRow>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </div>
  );
}
